import { useRef, useState } from 'react';
import copy from 'copy-to-clipboard';

import useEffectOnce from '#/utils/useEffectOnce';

interface Options {
  /**
   * Whether or not to transition from 'idle' status
   * to 'success' for a short period after copying the
   * text to the clipboard, then back to 'idle' status.
   */
  readonly enableFeedback?: boolean;
}

/**
 * The duration in milliseconds to stay in the success
 * state before reverting to the idle state.
 */
const FEEDBACK_DURATION = 3000;

export default function useCopyToClipboard(
  textToCopy: string,
  { enableFeedback = true }: Options = {},
) {
  const [status, setStatus] = useState<'idle' | 'success'>('idle');
  const feedbackTimeoutHandle = useRef<ReturnType<typeof setTimeout>>();

  function copyToClipboard() {
    cleanupTimeout();

    copy(textToCopy, { format: 'text/plain' });
    if (!enableFeedback) return;

    setStatus('success');

    feedbackTimeoutHandle.current = setTimeout(
      () => setStatus('idle'),
      FEEDBACK_DURATION,
    );
  }

  function cleanupTimeout() {
    if (feedbackTimeoutHandle.current == null) return;
    clearTimeout(feedbackTimeoutHandle.current);
  }

  useEffectOnce(() => cleanupTimeout);

  return [copyToClipboard, status] as const;
}
